import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { type Event } from 'nostr-tools';
import {
  BarChart3, Users, MessageCircle, Film, PenSquare, Heart,
  Repeat2, Coins, Gift, TrendingUp, Activity, Lock,
  RefreshCw, Calendar, ArrowUpRight, ArrowDownRight, Minus,
  ChevronDown
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';

// ── Access control ──────────────────────────────────────────────────
// Only these pubkeys can view the dashboard.
const ADMIN_PUBKEYS = [
  'df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc', // BCH Reply Guy
];

// ── Helpers ─────────────────────────────────────────────────────────

const BCH_TAGS = ['bch', 'bitcoincash'];

// Per-query relay fetch limits. When a result reaches (or exceeds) its limit,
// the relay almost certainly truncated older events, so the derived count is a
// floor — we flag it as "capped / ≈" rather than presenting it as exact.
const LIMITS = {
  // BCH-tagged posts = #bch (2000) + #bitcoincash (1000)
  taggedPosts: 3000,
  clientPosts: 3000,
  clips: 500,
  articles: 500,
  reactions: 2000,
  reposts: 500,
  tips: 500,
  referrals: 500,
  polls: 200,
};

// Number of most-recent posts we actually measure engagement (reactions /
// reposts) against. Engagement Rate is computed against THIS sample, not the
// full post count, so the ratio is internally consistent.
const ENGAGEMENT_SAMPLE = 200;

function dayKey(ts: number): string {
  const d = new Date(ts * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayLabel(key: string): string {
  const [, m, d] = key.split('-');
  return `${m}/${d}`;
}

/** Generate a list of day keys for the last N days */
function lastNDays(n: number): string[] {
  const days: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    days.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return days;
}

function countByDay(events: Event[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const e of events) {
    const key = dayKey(e.created_at);
    map.set(key, (map.get(key) || 0) + 1);
  }
  return map;
}

function uniquePubkeysByDay(events: Event[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const e of events) {
    const key = dayKey(e.created_at);
    if (!map.has(key)) map.set(key, new Set());
    map.get(key)!.add(e.pubkey);
  }
  return map;
}

function pctChange(current: number, previous: number): { pct: number; direction: 'up' | 'down' | 'flat' } {
  if (previous === 0 && current === 0) return { pct: 0, direction: 'flat' };
  if (previous === 0) return { pct: 100, direction: 'up' };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(pct), direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

// ── Mini SVG Sparkline Chart ────────────────────────────────────────

function SparklineChart({ data, days, color = '#0AC18E', height = 60 }: {
  data: Map<string, number>;
  days: string[];
  color?: string;
  height?: number;
}) {
  const values = days.map(d => data.get(d) || 0);
  const max = Math.max(...values, 1);
  const w = 100;
  const h = height;
  const padding = 2;

  const points = values.map((v, i) => {
    const x = padding + (i / Math.max(values.length - 1, 1)) * (w - padding * 2);
    const y = h - padding - (v / max) * (h - padding * 2);
    return `${x},${y}`;
  });

  const areaPoints = [
    `${padding},${h - padding}`,
    ...points,
    `${w - padding},${h - padding}`,
  ].join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      {/* Area fill */}
      <polygon points={areaPoints} fill={color} opacity="0.1" />
      {/* Line */}
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Last point dot */}
      {values.length > 0 && (() => {
        const lastX = padding + ((values.length - 1) / Math.max(values.length - 1, 1)) * (w - padding * 2);
        const lastY = h - padding - (values[values.length - 1] / max) * (h - padding * 2);
        return <circle cx={lastX} cy={lastY} r="2" fill={color} />;
      })()}
    </svg>
  );
}

// ── Bar Chart ───────────────────────────────────────────────────────

function BarChart({ data, days, color = '#0AC18E', height = 140 }: {
  data: Map<string, number>;
  days: string[];
  color?: string;
  height?: number;
}) {
  const values = days.map(d => data.get(d) || 0);
  const max = Math.max(...values, 1);
  const barWidth = Math.max(100 / days.length - 1, 2);
  const gap = Math.max(100 / days.length * 0.15, 0.5);

  // Show every Nth label to avoid overlap
  const labelEvery = days.length > 21 ? 7 : days.length > 14 ? 3 : days.length > 7 ? 2 : 1;

  return (
    <div>
      <svg viewBox={`0 0 100 ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
        {/* Horizontal grid lines */}
        {[0.25, 0.5, 0.75].map(pct => (
          <line key={pct} x1="0" x2="100" y1={height * (1 - pct) - 2} y2={height * (1 - pct) - 2}
            stroke="currentColor" strokeWidth="0.2" opacity="0.15" />
        ))}
        {/* Bars */}
        {values.map((v, i) => {
          const barH = Math.max((v / max) * (height - 20), v > 0 ? 1 : 0);
          const x = (i / days.length) * 100 + gap / 2;
          return (
            <g key={i}>
              <rect
                x={x}
                y={height - 14 - barH}
                width={barWidth}
                height={barH}
                rx="0.5"
                fill={color}
                opacity="0.8"
              />
              {/* Value label on hover — rendered as title */}
              <title>{dayLabel(days[i])}: {v}</title>
            </g>
          );
        })}
      </svg>
      {/* X-axis labels */}
      <div className="flex justify-between mt-1 px-0.5">
        {days.map((d, i) => (
          <span key={d} className="text-[8px] text-muted-foreground/50" style={{
            visibility: i % labelEvery === 0 || i === days.length - 1 ? 'visible' : 'hidden',
            width: `${100 / days.length}%`,
            textAlign: 'center',
          }}>
            {dayLabel(d)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Stat Card ───────────────────────────────────────────────────────

function StatCard({ label, value, change, icon: Icon, color, sparkData, days, subValue, approx }: {
  label: string;
  value: string | number;
  change?: { pct: number; direction: 'up' | 'down' | 'flat' };
  icon: React.ElementType;
  color: string;
  sparkData?: Map<string, number>;
  days?: string[];
  subValue?: string;
  /** True when the underlying query hit its relay limit — the value is a
   *  floor, not an exact count. Renders a "≈" and a "capped" hint. */
  approx?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {approx && (
            <span
              className="text-[9px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full"
              title="This query hit its relay fetch limit — the real number is at least this high."
            >
              capped
            </span>
          )}
          {change && change.direction !== 'flat' && (
            <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${
              change.direction === 'up' ? 'text-emerald-500' : 'text-red-400'
            }`}>
              {change.direction === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {change.pct}%
            </div>
          )}
          {change && change.direction === 'flat' && (
            <div className="flex items-center gap-0.5 text-[10px] font-semibold text-muted-foreground">
              <Minus className="w-3 h-3" /> 0%
            </div>
          )}
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold tabular-nums">{approx && typeof value === 'number' ? '≈ ' : ''}{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {subValue && <p className="text-[10px] text-muted-foreground mt-0.5">{subValue}</p>}
      </div>
      {sparkData && days && (
        <SparklineChart data={sparkData} days={days} color={color} height={40} />
      )}
    </div>
  );
}

// ── Cache for metrics snapshot ──────────────────────────────────────

const METRICS_CACHE_KEY = 'bchnostr:metrics:v1';

interface MetricsSnapshot {
  ts: number;
  posts: number;
  uniqueUsers: number;
  clips: number;
  articles: number;
  reactions: number;
  tips: number;
  referrals: number;
  polls: number;
  bchNostrPosts?: number;
  bchNostrUsers?: number;
}

function loadCachedMetrics(): MetricsSnapshot | null {
  try {
    const raw = localStorage.getItem(METRICS_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as MetricsSnapshot;
    // Cache valid for 5 minutes
    if (Date.now() - data.ts > 5 * 60 * 1000) return null;
    return data;
  } catch { return null; }
}

function saveCachedMetrics(m: MetricsSnapshot) {
  try { localStorage.setItem(METRICS_CACHE_KEY, JSON.stringify(m)); } catch {}
}

// ══════════════════════════════════════════════════════════════════════
// ██  METRICS PAGE
// ══════════════════════════════════════════════════════════════════════

export default function MetricsPage() {
  const { user } = useAppContext();
  const [timeRange, setTimeRange] = useState<14 | 30 | 60>(30);

  // ── Access gate ──
  if (!user || !ADMIN_PUBKEYS.includes(user.pubkey)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4 p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-lg font-bold">Private Dashboard</h2>
        <p className="text-sm text-muted-foreground max-w-xs">
          This page is only accessible to site administrators.
        </p>
      </div>
    );
  }

  const days = useMemo(() => lastNDays(timeRange), [timeRange]);
  const sinceTs = useMemo(() => Math.floor(Date.now() / 1000) - timeRange * 86400, [timeRange]);

  // ── Data fetching ──────────────────────────────────────────────────
  // Fetch BCH-tagged posts (kind 1) — the wider BCH conversation across
  // any Nostr client, identified by the #bch / #bitcoincash hashtags.
  const { events: posts, loading: loadingPosts } = useNostrQuery({
    filters: [
      { kinds: [1], '#t': ['bch'], limit: 2000, since: sinceTs }, // 2000 + 1000 = LIMITS.taggedPosts
      { kinds: [1], '#t': ['bitcoincash'], limit: 1000, since: sinceTs },
    ],
    allRelays: true,
  });

  // Fetch posts made *from* BCH Nostr (kind 1 + 1068), identified by the
  // #bchnostr tag every in-app post now carries. We use the 't' tag rather
  // than the 'client' tag because relays index single-letter tags for
  // filtering; a '#client' filter returns little to nothing on most relays.
  const { events: clientPosts, loading: loadingClientPosts } = useNostrQuery({
    filters: [
      { kinds: [1, 1068], '#t': ['bchnostr'], limit: LIMITS.clientPosts, since: sinceTs },
    ],
    allRelays: true,
  });

  // Fetch video clips (kinds 22, 34236)
  const { events: clips, loading: loadingClips } = useNostrQuery({
    filters: [
      { kinds: [22, 34236], '#t': BCH_TAGS, limit: LIMITS.clips, since: sinceTs },
    ],
    allRelays: true,
    extraRelays: ['wss://relay.divine.video'],
  });

  // Fetch articles (kind 30023)
  const { events: articles, loading: loadingArticles } = useNostrQuery({
    filters: [
      { kinds: [30023], '#t': ['bch'], limit: LIMITS.articles, since: sinceTs },
    ],
    allRelays: true,
  });

  // Fetch reactions (kind 7) and reposts (kind 6) against the most recent
  // ENGAGEMENT_SAMPLE posts. Engagement Rate below is measured against exactly
  // this sample so the ratio is meaningful (not engagement-on-200 ÷ all-posts).
  const engagementSampleIds = useMemo(
    () => posts.slice(0, ENGAGEMENT_SAMPLE).map(e => e.id),
    [posts.length],
  );
  const { events: reactions } = useNostrQuery({
    filters: engagementSampleIds.length > 0 ? [{ kinds: [7], '#e': engagementSampleIds, limit: LIMITS.reactions, since: sinceTs }] : [],
    enabled: engagementSampleIds.length > 0,
    allRelays: true,
  });

  const { events: reposts } = useNostrQuery({
    filters: engagementSampleIds.length > 0 ? [{ kinds: [6], '#e': engagementSampleIds, limit: LIMITS.reposts, since: sinceTs }] : [],
    enabled: engagementSampleIds.length > 0,
    allRelays: true,
  });

  // Fetch tips (kind 9735)
  const { events: tips } = useNostrQuery({
    filters: [{ kinds: [9735], '#t': ['bch-tip'], limit: LIMITS.tips, since: sinceTs }],
    allRelays: true,
  });

  // Fetch referrals (kind 30078 with d=bchnostr/referral)
  const { events: referrals } = useNostrQuery({
    filters: [{ kinds: [30078], '#d': ['bchnostr/referral'], limit: LIMITS.referrals, since: sinceTs }],
    allRelays: true,
  });

  // Fetch polls (kind 1068)
  const { events: polls } = useNostrQuery({
    filters: [{ kinds: [1068], '#t': ['bch'], limit: LIMITS.polls, since: sinceTs }],
    allRelays: true,
  });

  // ── Deduplicate posts (bch and bitcoincash tags overlap) ──
  const dedupedPosts = useMemo(() => {
    const seen = new Set<string>();
    return posts.filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
  }, [posts.length]);

  // ── Deduplicate BCH Nostr client posts ──
  const dedupedClientPosts = useMemo(() => {
    const seen = new Set<string>();
    return clientPosts.filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
  }, [clientPosts.length]);

  // ── Aggregate data by day ──
  const postsByDay = useMemo(() => countByDay(dedupedPosts), [dedupedPosts.length]);
  const usersByDay = useMemo(() => uniquePubkeysByDay(dedupedPosts), [dedupedPosts.length]);
  const clientPostsByDay = useMemo(() => countByDay(dedupedClientPosts), [dedupedClientPosts.length]);
  const clipsByDay = useMemo(() => countByDay(clips), [clips.length]);
  const articlesByDay = useMemo(() => countByDay(articles), [articles.length]);
  const reactionsByDay = useMemo(() => countByDay(reactions), [reactions.length]);
  const repostsByDay = useMemo(() => countByDay(reposts), [reposts.length]);
  const tipsByDay = useMemo(() => countByDay(tips), [tips.length]);
  const referralsByDay = useMemo(() => countByDay(referrals), [referrals.length]);
  const pollsByDay = useMemo(() => countByDay(polls), [polls.length]);

  // Convert usersByDay (Set<string>) to numeric Map for charting
  const uniqueUsersByDay = useMemo(() => {
    const map = new Map<string, number>();
    usersByDay.forEach((set, key) => map.set(key, set.size));
    return map;
  }, [usersByDay]);

  // ── Summary stats ──
  const today = dayKey(Math.floor(Date.now() / 1000));
  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const totalPosts = dedupedPosts.length;
  const totalUniqueUsers = new Set(dedupedPosts.map(e => e.pubkey)).size;
  const totalClientPosts = dedupedClientPosts.length;
  const totalClientUsers = new Set(dedupedClientPosts.map(e => e.pubkey)).size;

  // ── "Capped" detection ──────────────────────────────────────────────
  // A count is a floor (not exact) when its query hit the relay fetch limit.
  // We compare the RAW fetched length (pre-dedupe) against the limit, since
  // dedupe/merge across relays can push the merged array above the single-
  // filter cap. Use >= with a small margin to be safe.
  const capped = (rawLen: number, limit: number) => rawLen >= limit;
  const approx = {
    taggedPosts: capped(posts.length, LIMITS.taggedPosts),
    clientPosts: capped(clientPosts.length, LIMITS.clientPosts),
    users: capped(posts.length, LIMITS.taggedPosts), // derived from posts
    clientUsers: capped(clientPosts.length, LIMITS.clientPosts),
    clips: capped(clips.length, LIMITS.clips),
    articles: capped(articles.length, LIMITS.articles),
    reactions: capped(reactions.length, LIMITS.reactions),
    reposts: capped(reposts.length, LIMITS.reposts),
    tips: capped(tips.length, LIMITS.tips),
    referrals: capped(referrals.length, LIMITS.referrals),
    polls: capped(polls.length, LIMITS.polls),
  };
  // Whether anything on the page is capped — drives the header disclaimer.
  const anyCapped = Object.values(approx).some(Boolean);
  const totalClips = clips.length;
  const totalArticles = articles.length;
  const totalReactions = reactions.length;
  const totalReposts = reposts.length;
  const totalTips = tips.length;
  const totalReferrals = referrals.length;
  const totalPolls = polls.length;

  // Today vs yesterday for change indicators
  const postsToday = postsByDay.get(today) || 0;
  const postsYesterday = postsByDay.get(yesterday) || 0;
  const clientPostsToday = clientPostsByDay.get(today) || 0;
  const clientPostsYesterday = clientPostsByDay.get(yesterday) || 0;
  const usersToday = uniqueUsersByDay.get(today) || 0;
  const usersYesterday = uniqueUsersByDay.get(yesterday) || 0;
  const clipsToday = clipsByDay.get(today) || 0;
  const clipsYesterday = clipsByDay.get(yesterday) || 0;
  const reactionsToday = reactionsByDay.get(today) || 0;
  const reactionsYesterday = reactionsByDay.get(yesterday) || 0;

  // Tip sats calculation
  const totalTipSats = useMemo(() => {
    let total = 0;
    for (const e of tips) {
      try {
        const content = JSON.parse(e.content);
        total += content.amount_sats || 0;
      } catch {
        const amountTag = e.tags.find(t => t[0] === 'amount');
        if (amountTag) total += parseInt(amountTag[1]) || 0;
      }
    }
    return total;
  }, [tips.length]);

  // Cumulative unique users over time
  const cumulativeUsers = useMemo(() => {
    const allPubkeys = new Set<string>();
    const map = new Map<string, number>();
    // Sort events by time to build cumulative
    const sorted = [...dedupedPosts].sort((a, b) => a.created_at - b.created_at);
    for (const e of sorted) {
      allPubkeys.add(e.pubkey);
      const key = dayKey(e.created_at);
      map.set(key, allPubkeys.size);
    }
    // Fill forward
    let last = 0;
    for (const d of days) {
      if (map.has(d)) last = map.get(d)!;
      else map.set(d, last);
    }
    return map;
  }, [dedupedPosts.length, days]);

  // New users per day (first-time posters)
  const newUsersByDay = useMemo(() => {
    const seen = new Set<string>();
    const map = new Map<string, number>();
    const sorted = [...dedupedPosts].sort((a, b) => a.created_at - b.created_at);
    for (const e of sorted) {
      if (!seen.has(e.pubkey)) {
        seen.add(e.pubkey);
        const key = dayKey(e.created_at);
        map.set(key, (map.get(key) || 0) + 1);
      }
    }
    return map;
  }, [dedupedPosts.length]);

  // Engagement rate: (reactions + reposts) / posts sampled for engagement.
  // We divide by the SAME sample the numerator was measured against
  // (the most recent `engagementSampleIds`), not the full post count — that
  // earlier mismatch made the rate meaningless. It's an average of
  // (likes + reposts) per recent post.
  const engagementSampleSize = engagementSampleIds.length;
  const engagementRate = engagementSampleSize > 0
    ? ((totalReactions + totalReposts) / engagementSampleSize * 100).toFixed(1)
    : '0';

  const isLoading = loadingPosts || loadingClientPosts || loadingClips || loadingArticles;

  // Cache snapshot
  useEffect(() => {
    if (!isLoading && totalPosts > 0) {
      saveCachedMetrics({
        ts: Date.now(),
        posts: totalPosts,
        uniqueUsers: totalUniqueUsers,
        clips: totalClips,
        articles: totalArticles,
        reactions: totalReactions,
        tips: totalTips,
        referrals: totalReferrals,
        polls: totalPolls,
        bchNostrPosts: totalClientPosts,
        bchNostrUsers: totalClientUsers,
      });
    }
  }, [isLoading, totalPosts]);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center shadow-lg shadow-primary/20">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold flex items-center gap-1.5">
                Site Metrics
                <span className="text-[9px] bg-amber-500/15 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full font-semibold">PRIVATE</span>
              </h1>
              <p className="text-[10px] text-muted-foreground">BCH Nostr community dashboard · relay-sampled estimates</p>
            </div>
          </div>

          {/* Time range selector */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
            {([14, 30, 60] as const).map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                  timeRange === range
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {range}d
              </button>
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Fetching data from relays...
          </div>
        )}
        {!isLoading && anyCapped && (
          <p className="mt-2 text-[10px] leading-relaxed text-amber-600 dark:text-amber-400">
            Some counts hit their relay fetch limit and are marked{' '}
            <span className="font-semibold">“capped” / “≈”</span> — the true numbers are at least this high.
            These are directional estimates gathered live from relays, not an exact census.
          </p>
        )}
      </div>

      <div className="p-4 space-y-6">
        {/* ── Overview Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <StatCard
            label="BCH Nostr Posts"
            value={totalClientPosts}
            change={pctChange(clientPostsToday, clientPostsYesterday)}
            icon={MessageCircle}
            color="#0AC18E"
            sparkData={clientPostsByDay}
            days={days}
            subValue={`${clientPostsToday} today · from BCH Nostr`}
            approx={approx.clientPosts}
          />
          <StatCard
            label="BCH-Tagged Posts"
            value={totalPosts}
            change={pctChange(postsToday, postsYesterday)}
            icon={MessageCircle}
            color="#14B8A6"
            sparkData={postsByDay}
            days={days}
            subValue={`${postsToday} today · #bch across Nostr`}
            approx={approx.taggedPosts}
          />
          <StatCard
            label="Unique Users"
            value={totalUniqueUsers}
            change={pctChange(usersToday, usersYesterday)}
            icon={Users}
            color="#6366F1"
            sparkData={uniqueUsersByDay}
            days={days}
            subValue={`${usersToday} active today`}
            approx={approx.users}
          />
          <StatCard
            label="Cash Clips"
            value={totalClips}
            change={pctChange(clipsToday, clipsYesterday)}
            icon={Film}
            color="#F59E0B"
            sparkData={clipsByDay}
            days={days}
            approx={approx.clips}
          />
          <StatCard
            label="BCH Ink Articles"
            value={totalArticles}
            icon={PenSquare}
            color="#3B82F6"
            sparkData={articlesByDay}
            days={days}
            approx={approx.articles}
          />
          <StatCard
            label="Reactions"
            value={totalReactions}
            change={pctChange(reactionsToday, reactionsYesterday)}
            icon={Heart}
            color="#EF4444"
            sparkData={reactionsByDay}
            days={days}
            subValue={`on last ${engagementSampleSize} posts`}
            approx={approx.reactions}
          />
          <StatCard
            label="Reposts"
            value={totalReposts}
            icon={Repeat2}
            color="#8B5CF6"
            sparkData={repostsByDay}
            days={days}
            subValue={`on last ${engagementSampleSize} posts`}
            approx={approx.reposts}
          />
          <StatCard
            label="BCH Tips"
            value={totalTips}
            icon={Coins}
            color="#10B981"
            sparkData={tipsByDay}
            days={days}
            subValue={totalTipSats > 0 ? `${totalTipSats.toLocaleString()} sats total` : undefined}
            approx={approx.tips}
          />
          <StatCard
            label="Referrals"
            value={totalReferrals}
            icon={Gift}
            color="#EC4899"
            sparkData={referralsByDay}
            approx={approx.referrals}
            days={days}
          />
        </div>

        {/* ── Key metrics row ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Engagement Rate</p>
            <p className="text-xl font-bold mt-1">{engagementRate}%</p>
            <p className="text-[10px] text-muted-foreground">(likes+reposts) / last {engagementSampleSize} posts</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">BCH Nostr Users</p>
            <p className="text-xl font-bold mt-1">{totalClientUsers.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">posted from the app</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Polls Created</p>
            <p className="text-xl font-bold mt-1">{totalPolls}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Posts/User</p>
            <p className="text-xl font-bold mt-1">{totalUniqueUsers > 0 ? (totalPosts / totalUniqueUsers).toFixed(1) : '0'}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3 text-center">
            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Avg Posts/Day</p>
            <p className="text-xl font-bold mt-1">{days.length > 0 ? Math.round(totalPosts / days.length) : 0}</p>
          </div>
        </div>

        {/* ── Posts per Day (bar chart) ── */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" /> Posts per Day
            </h3>
            <span className="text-[10px] text-muted-foreground">{timeRange} day view</span>
          </div>
          <BarChart data={postsByDay} days={days} color="#0AC18E" height={160} />
        </div>

        {/* ── Active Users per Day ── */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-500" /> Daily Active Users
            </h3>
          </div>
          <BarChart data={uniqueUsersByDay} days={days} color="#6366F1" height={140} />
        </div>

        {/* ── Cumulative Users ── */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Cumulative Unique Users
            </h3>
            <span className="text-xs font-bold text-emerald-500">{totalUniqueUsers} total</span>
          </div>
          <SparklineChart data={cumulativeUsers} days={days} color="#10B981" height={120} />
          <div className="flex justify-between mt-1 px-0.5">
            <span className="text-[8px] text-muted-foreground/50">{dayLabel(days[0])}</span>
            <span className="text-[8px] text-muted-foreground/50">{dayLabel(days[days.length - 1])}</span>
          </div>
        </div>

        {/* ── New Users per Day ── */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Gift className="w-4 h-4 text-pink-500" /> New Users per Day
              <span className="text-[10px] text-muted-foreground font-normal">(first-time posters)</span>
            </h3>
          </div>
          <BarChart data={newUsersByDay} days={days} color="#EC4899" height={120} />
        </div>

        {/* ── Engagement: Reactions per Day ── */}
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" /> Reactions per Day
            </h3>
          </div>
          <BarChart data={reactionsByDay} days={days} color="#EF4444" height={120} />
        </div>

        {/* ── Tips per Day ── */}
        {totalTips > 0 && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Coins className="w-4 h-4 text-emerald-500" /> BCH Tips per Day
              </h3>
              <span className="text-xs font-bold text-emerald-500">{totalTipSats.toLocaleString()} sats</span>
            </div>
            <BarChart data={tipsByDay} days={days} color="#10B981" height={120} />
          </div>
        )}

        {/* ── Top Posters ── */}
        <TopPostersTable events={dedupedPosts} />

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[10px] text-muted-foreground/40">
            Data sourced live from Nostr relays. Numbers may vary based on relay availability.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Top Posters Table ───────────────────────────────────────────────

function TopPostersTable({ events }: { events: Event[] }) {
  const [expanded, setExpanded] = useState(false);

  const topPosters = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of events) {
      counts.set(e.pubkey, (counts.get(e.pubkey) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, expanded ? 25 : 10)
      .map(([pubkey, count]) => ({ pubkey, count }));
  }, [events.length, expanded]);

  if (topPosters.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-bold flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" /> Top Posters
        </h3>
        <span className="text-[10px] text-muted-foreground">{events.length} posts from {new Set(events.map(e => e.pubkey)).size} users</span>
      </div>
      <div className="divide-y divide-border/50">
        {topPosters.map((p, i) => (
          <TopPosterRow key={p.pubkey} rank={i + 1} pubkey={p.pubkey} count={p.count} maxCount={topPosters[0]?.count || 1} />
        ))}
      </div>
      {topPosters.length >= 10 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full px-4 py-2 text-xs text-primary font-medium hover:bg-muted/50 transition-colors flex items-center justify-center gap-1"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function TopPosterRow({ rank, pubkey, count, maxCount }: { rank: number; pubkey: string; count: number; maxCount: number }) {
  const { profile } = useNostrProfile(pubkey);
  const name = profile?.display_name || profile?.name || pubkey.slice(0, 12) + '...';
  const barWidth = Math.max((count / maxCount) * 100, 2);

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className={`w-5 text-right text-xs font-bold ${rank <= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
        {rank}
      </span>
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Users className="w-3.5 h-3.5 text-primary/60" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate">{name}</p>
        <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${barWidth}%` }} />
        </div>
      </div>
      <span className="text-xs font-bold tabular-nums shrink-0">{count}</span>
    </div>
  );
}



